import React, {useEffect, useState} from 'react';
import * as z from 'zod';
import {ArrowRight, Loader2} from 'lucide-react';

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {toast} from '@/hooks/use-toast';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {useLanguage} from '@/contexts/LanguageContext';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useUserInfo} from '@/hooks/useUserInfo.ts';
import {getSummary} from '@/utils/pasUtils.js';
import {sendEmail} from '@/utils/smtp2go.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const departments_list = ['تطوير الأعمال'];
const visit_types_list = ['تسليم / استلام', 'معالجة شكوى', 'مسح ميداني / تفتيش', 'أخرى'];
const visit_status_list = ['مكتملة', 'تحتاج متابعة', 'لم يتم اللقاء بالمعني'];

const FIELD_VISIT_RECIPIENT = 'muna@wsmco.sa';

function buildFieldVisitEmailHtml(values: {
  department: string;
  employee: string;
  visit_date: string;
  start_time?: string;
  end_time?: string;
  visited_entity: string;
  visit_location?: string;
  visit_type?: string;
  visit_purpose: string;
  visit_summary?: string;
  recommendations?: string;
  contact_number?: string;
  visit_status?: string;
}) {
  const row = (label: string, value?: string) => `
    <tr>
      <td style="padding:10px 14px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:180px;">${label}</td>
      <td style="padding:10px 14px;border:1px solid #e5e7eb;">${value && value.trim() ? value : '—'}</td>
    </tr>`;

  return `
  <div dir="rtl" style="font-family:Tahoma,Arial,sans-serif;color:#1f2937;max-width:640px;margin:0 auto;">
    <h2 style="margin-bottom:4px;">خدمة زيارة ميدانية</h2>
    <p style="color:#6b7280;margin-top:0;">تم تقديم تقرير زيارة ميدانية جديد عبر بوابة الموظفين.</p>

    <h3 style="margin-bottom:8px;">بيانات الموظف</h3>
    <table style="border-collapse:collapse;width:100%;margin-bottom:20px;">
      ${row('القسم / الإدارة', values.department)}
      ${row('اسم الموظف', values.employee)}
      ${row('تاريخ الزيارة', values.visit_date)}
      ${row('وقت البدء', values.start_time)}
      ${row('وقت الانتهاء', values.end_time)}
    </table>

    <h3 style="margin-bottom:8px;">بيانات الزيارة</h3>
    <table style="border-collapse:collapse;width:100%;margin-bottom:20px;">
      ${row('اسم الجهة / الشخص الذي تمت زيارته', values.visited_entity)}
      ${row('موقع الزيارة', values.visit_location)}
      ${row('نوع الزيارة', values.visit_type)}
      ${row('الغرض من الزيارة', values.visit_purpose)}
    </table>

    <h3 style="margin-bottom:8px;">نتائج الزيارة</h3>
    <table style="border-collapse:collapse;width:100%;">
      ${row('ملخص ما تم خلال الزيارة', values.visit_summary)}
      ${row('التوصيات / الإجراء المطلوب', values.recommendations)}
      ${row('رقم التواصل', values.contact_number)}
      ${row('حالة الزيارة', values.visit_status)}
    </table>
  </div>`;
}

const formSchema = z
    .object({
        department: z.string({
            required_error: 'القسم / الإدارة is required.',
        }).min(1, 'القسم / الإدارة مطلوب'),
        employee: z.string({
            required_error: 'اسم الموظف is required.',
        }).min(1, 'اسم الموظف مطلوب'),
        visit_date: z.string({
            required_error: 'تاريخ الزيارة is required.',
        }).min(1, 'تاريخ الزيارة مطلوب'),
        start_time: z.string().optional(),
        end_time: z.string().optional(),
        visited_entity: z.string({
            required_error: 'اسم الجهة / الشخص الذي تمت زيارته is required.',
        }).min(1, 'اسم الجهة / الشخص الذي تمت زيارته مطلوب'),
        visit_location: z.string().optional(),
        visit_type: z.string().optional(),
        visit_purpose: z.string({
            required_error: 'الغرض من الزيارة is required.',
        }).min(1, 'الغرض من الزيارة مطلوب'),
        visit_summary: z.string().optional(),
        recommendations: z.string().optional(),
        contact_number: z
            .string()
            .optional()
            .refine((val) => !val || /^[0-9+\- ]{7,15}$/.test(val), {
                message: 'رقم التواصل غير صحيح',
            }),
        visit_status: z.string().optional(),
    })
    .refine(
        (data) => {
            if (!data.start_time || !data.end_time) return true;
            return data.end_time > data.start_time;
        },
        {
            message: 'وقت الانتهاء يجب أن يكون بعد وقت البدء',
            path: ['end_time'],
        }
    );

const ServiceFieldVisit = () => {
    const VITE_USERNAMET = import.meta.env.VITE_USERNAMET;
    const VITE_PASSWORD = import.meta.env.VITE_PASSWORD;

    const {t} = useLanguage();
    const navigate = useNavigate();
    const [data, setData] = useState({
        profile: null,
        loading: true,
        error: null,
    });
    const {email} = useUserInfo();
    const [selectedVisitDate, setSelectedVisitDate] = useState<Date | null>(new Date());
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            department: departments_list[0],
            employee: data.profile ? `${data.profile.full_name_arabic}` : '',
            visit_date: new Date().toLocaleDateString('en-CA'),
            start_time: '',
            end_time: '',
            visited_entity: '',
            visit_location: '',
            visit_type: 'متابعة',
            visit_purpose: '',
            visit_summary: '',
            recommendations: '',
            contact_number: '',
            visit_status: 'مكتملة',
        },
    });

    useEffect(() => {
        async function fetchProfile() {
            try {
                if (email) {
                    const summaryResponse = await getSummary(VITE_USERNAMET, VITE_PASSWORD, email);
                    if (data.loading) {
                        setData((prev) => ({
                            ...prev,
                            profile: summaryResponse.profile,
                            loading: false,
                            error: null,
                        }));
                        form.reset({
                            department: departments_list[0],
                            employee: `${summaryResponse.profile.full_name_arabic}`,
                            visit_date: new Date().toLocaleDateString('en-CA'),
                            start_time: '',
                            end_time: '',
                            visited_entity: '',
                            visit_location: '',
                            visit_type: 'متابعة',
                            visit_purpose: '',
                            visit_summary: '',
                            recommendations: '',
                            contact_number: '',
                            visit_status: 'مكتملة',
                        });
                    }
                }
            } catch (err) {
                setData((prev) => ({...prev, loading: false, error: err.message || 'Failed to fetch profile'}));
                console.log(err.message || 'Failed to fetch profile');
            }
        }

        fetchProfile();
    }, [VITE_PASSWORD, VITE_USERNAMET, data.loading, email, form]);

    async function onSubmit(values: z.infer<typeof formSchema>) {
        setIsSubmitting(true);
        try {
            await sendEmail({
                to: FIELD_VISIT_RECIPIENT,
                subject: `خدمة زيارة ميدانية - ${values.employee} - ${values.visit_date}`,
                htmlBody: buildFieldVisitEmailHtml(values),
            });
            toast({
                title: 'تم إرسال الطلب بنجاح',
                description: 'تم إرسال تقرير الزيارة الميدانية عبر البريد الإلكتروني.',
                duration: 5000,
            });
            navigate('/services');
        } catch (error) {
            console.error('Failed to send field visit email:', error);
            toast({
                title: 'فشل في إرسال الطلب',
                description: error.message || 'حدث خطأ أثناء إرسال البريد الإلكتروني. يرجى المحاولة مرة أخرى.',
                duration: 5000,
                variant: 'destructive',
            });
            setIsSubmitting(false);
        }
    }

    return (
        <>
            {data.loading ? (
                <div>Loading...</div>
            ) : data.profile ? (
                <div className="w-full max-w-4xl">
                    <div className="flex items-center gap-4 mb-6">
                        <Button variant="ghost" onClick={() => navigate('/services')} className="gap-2">
                            <ArrowRight className="h-4 w-4"/>
                            العودة للخدمات
                        </Button>
                    </div>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-2xl font-bold text-right">بيانات الموظف</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormField
                                            control={form.control}
                                            name="department"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">القسم / الإدارة</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger
                                                                className="w-full p-2 border rounded-md text-right flex justify-between flex-row-reverse">
                                                                <SelectValue placeholder="اختر القسم / الإدارة"/>
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {departments_list.map((dept) => (
                                                                <SelectItem key={dept} value={dept}>
                                                                    {dept}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        {data.profile.name && (
                                            <FormField
                                                control={form.control}
                                                name="employee"
                                                render={({field}) => (
                                                    <FormItem>
                                                        <FormLabel className="text-right block">اسم الموظف</FormLabel>
                                                        <FormControl>
                                                            <Input readOnly placeholder="اسم الموظف..."
                                                                   className="text-right" {...field} />
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )}
                                            />
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                        <FormField
                                            control={form.control}
                                            name="visit_date"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">تاريخ الزيارة</FormLabel>
                                                    <FormControl>
                                                        <DatePicker
                                                            selected={selectedVisitDate}
                                                            onChange={(date: Date | null) => {
                                                                setSelectedVisitDate(date);
                                                                field.onChange(date ? date.toLocaleDateString('en-CA') : '');
                                                            }}
                                                            dateFormat="yyyy/MM/dd"
                                                            placeholderText="اختر تاريخ الزيارة..."
                                                            className="w-full p-2 border rounded-md text-right"
                                                            wrapperClassName="w-full"
                                                        />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="start_time"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">وقت البدء</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="time"
                                                            step="300"
                                                            className="text-right text-lg flex justify-between flex-row-reverse"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="end_time"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">وقت الانتهاء</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="time"
                                                            step="300"
                                                            className="text-right text-lg flex justify-between flex-row-reverse"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-2xl font-bold text-right">بيانات الزيارة</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormField
                                            control={form.control}
                                            name="visited_entity"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">اسم الجهة / الشخص الذي تمت
                                                        زيارته</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="اسم الشخص أو الجهة"
                                                               className="text-right" {...field} />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="visit_location"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">موقع الزيارة</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="المدينة / الحي / العنوان"
                                                               className="text-right" {...field} />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="visit_type"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">نوع الزيارة</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger
                                                                className="w-full p-2 border  rounded-md text-right flex justify-between flex-row-reverse">
                                                                <SelectValue placeholder="اختر نوع الزيارة"/>
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {visit_types_list.map((type) => (
                                                                <SelectItem key={type} value={type}>
                                                                    {type}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="mt-6">
                                        <FormField
                                            control={form.control}
                                            name="visit_purpose"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">الغرض من الزيارة</FormLabel>
                                                    <FormControl>
                                                        <Textarea
                                                            placeholder="اذكر سبب الزيارة بإيجاز"
                                                            className="text-right"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-2xl font-bold text-right">نتائج الزيارة</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <FormField
                                        control={form.control}
                                        name="visit_summary"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel className="text-right block">ملخص ما تم خلال
                                                    الزيارة</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="وصف مختصر لما جرى خلال الزيارة"
                                                        className="text-right"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="recommendations"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel className="text-right block">التوصيات / الإجراء
                                                    المطلوب</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="أي توصيات أو متابعة مطلوبة"
                                                        className="text-right"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                        <FormField
                                            control={form.control}
                                            name="visit_status"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">حالة الزيارة</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger
                                                                className="w-full p-2 border  rounded-md text-right flex justify-between flex-row-reverse">
                                                                <SelectValue placeholder="اختر حالة الزيارة"/>
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {visit_status_list.map((status) => (
                                                                <SelectItem key={status} value={status}>
                                                                    {status}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="contact_number"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-right block">رقم التواصل
                                                        (اختياري)</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder="رقم جوال للتواصل إن لزم"
                                                            className="text-right"
                                                            dir="ltr"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                    </div>
                                </CardContent>
                            </Card>

                            <div className="flex gap-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => navigate('/services')} disabled={isSubmitting}>
                                    إلغاء
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin"/>}
                                    {isSubmitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            ) : (
                <div>No profile data available</div>
            )}
        </>
    );
};

export default ServiceFieldVisit;
